import {
  LogOut,
  MapPin,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  ShieldCheck,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "../auth";
import { navigationFor } from "../navigation";
import { apiRequest } from "../api";

const inactivityMs = 30 * 60_000;
const sidebarStorageKey = "rbim.portal.sidebar-collapsed";

export function PortalLayout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const [desktopSidebar, setDesktopSidebar] = useState(isDesktopSidebarViewport);
  const [serverAvailable, setServerAvailable] = useState<boolean | null>(null);
  const isBarangayPortal = user?.role === "BARANGAY_ENCODER" || user?.role === "BARANGAY_SUPERVISOR";
  const isMayorPortal = user?.role === "MAYOR_VIEWER";
  const canCollapseSidebar = Boolean(isBarangayPortal || isMayorPortal);
  const collapsedSidebar = Boolean(canCollapseSidebar && desktopSidebar && sidebarCollapsed);

  useEffect(() => {
    let timeout = window.setTimeout(() => void logout(), inactivityMs);
    const reset = () => { window.clearTimeout(timeout); timeout = window.setTimeout(() => void logout(), inactivityMs); };
    const events = ["pointerdown", "keydown", "scroll"] as const;
    events.forEach((event) => window.addEventListener(event, reset, { passive: true }));
    return () => { window.clearTimeout(timeout); events.forEach((event) => window.removeEventListener(event, reset)); };
  }, [logout]);

  useEffect(() => {
    let active = true;
    apiRequest<{ status: string }>("/health")
      .then((response) => { if (active) setServerAvailable(response.status === "ok"); })
      .catch(() => { if (active) setServerAvailable(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 761px)");
    const updateViewport = () => setDesktopSidebar(media.matches);
    updateViewport();
    media.addEventListener("change", updateViewport);
    return () => media.removeEventListener("change", updateViewport);
  }, []);

  useEffect(() => {
    if (!canCollapseSidebar) return;
    try {
      window.localStorage.setItem(sidebarStorageKey, sidebarCollapsed ? "true" : "false");
    } catch {
      // Sidebar persistence is optional; the toggle still works for this session.
    }
  }, [canCollapseSidebar, sidebarCollapsed]);

  if (!user) return null;
  const items = navigationFor(user);
  const isDashboard = location.pathname === "/";
  const isSystemAdministrator = user.role === "SYSTEM_ADMINISTRATOR";
  const isPlatformAdministrator = isSystemAdministrator || user.role === "TECHNICAL_ADMINISTRATOR";
  const portalLabel = isMayorPortal
    ? "Mayor Viewer Portal"
    : isBarangayPortal
      ? "Barangay Web Portal"
      : isPlatformAdministrator
        ? "Platform Administration Portal"
        : "Municipality/City Web Portal";
  const accessNote = isMayorPortal
    ? "Read-only access to the approved Municipality/City RBIM dashboard."
    : isBarangayPortal
      ? "Access is locked to your assigned Barangay on every API request."
      : isSystemAdministrator
        ? "System administration has full application capability within approved geographic scope; all sensitive actions are audited."
        : isPlatformAdministrator
          ? "Platform administration is separated from routine resident-record operations."
          : "Access is checked by permission and approved location on every API request.";
  const locationContext = isSystemAdministrator
    ? { label: "Platform coverage", value: "Approved municipalities/cities" }
    : { label: "Assigned location", value: user.locationLabel };

  return (
    <div
      className="portal-shell"
      style={collapsedSidebar ? { gridTemplateColumns: "72px minmax(0, 1fr)" } : undefined}
    >
      <aside className={menuOpen ? "sidebar sidebar-open" : "sidebar"}>
        <div
          className="brand-block"
          style={collapsedSidebar ? { flexDirection: "column", justifyContent: "center", gap: 8, padding: "0 0 18px" } : undefined}
        >
          <div className="brand-logos">
            <img src="/logos/RBIM.png" alt="RBIM" />
            {!collapsedSidebar ? <img src="/logos/CPD.png" alt="Commission on Population and Development" /> : null}
          </div>
          {!collapsedSidebar ? <div><strong>RBIM Online</strong><span>{portalLabel}</span></div> : null}
          {canCollapseSidebar && desktopSidebar ? (
            <button
              className="icon-button"
              style={!collapsedSidebar ? { marginLeft: "auto" } : undefined}
              type="button"
              onClick={() => setSidebarCollapsed((current) => !current)}
              aria-label={collapsedSidebar ? "Expand sidebar" : "Collapse sidebar"}
              title={collapsedSidebar ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsedSidebar ? <PanelLeftOpen aria-hidden="true" /> : <PanelLeftClose aria-hidden="true" />}
            </button>
          ) : null}
          <button className="icon-button mobile-only" onClick={() => setMenuOpen(false)} aria-label="Close navigation"><X /></button>
        </div>
        <nav aria-label="Main navigation">
          {items.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              onClick={() => setMenuOpen(false)}
              title={collapsedSidebar ? item.label : undefined}
              aria-label={collapsedSidebar ? item.label : undefined}
              style={collapsedSidebar ? { justifyContent: "center", paddingLeft: 0, paddingRight: 0 } : undefined}
              className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
            >
              <item.icon aria-hidden="true" />
              {!collapsedSidebar ? <span>{item.label}</span> : null}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div
            className={`sidebar-server-status ${serverAvailable === false ? "offline" : ""}`}
            style={collapsedSidebar ? { justifyContent: "center", padding: "10px 0" } : undefined}
            title={collapsedSidebar ? (serverAvailable === false ? "RBIM Server unavailable" : serverAvailable ? "Connected to RBIM Server" : "Checking RBIM Server") : undefined}
          >
            {serverAvailable === false ? <WifiOff aria-hidden="true" /> : <Wifi aria-hidden="true" />}
            {!collapsedSidebar ? (
              <span><strong>{serverAvailable === null ? "Checking RBIM Server" : serverAvailable ? "Connected to RBIM Server" : "RBIM Server unavailable"}</strong><small>{serverAvailable ? "Server connection is available." : serverAvailable === false ? "Some online functions may be unavailable." : "Verifying secure connection…"}</small></span>
            ) : null}
          </div>
          <div
            className="sidebar-note"
            style={collapsedSidebar ? { justifyContent: "center", padding: "10px 0" } : undefined}
            title={collapsedSidebar ? accessNote : undefined}
          >
            <ShieldCheck />
            {!collapsedSidebar ? <span>{accessNote}</span> : null}
          </div>
        </div>
      </aside>
      {menuOpen && <button className="scrim" aria-label="Close navigation overlay" onClick={() => setMenuOpen(false)} />}
      <div className="portal-main">
        <header className="topbar">
          <button className="icon-button mobile-only" onClick={() => setMenuOpen(true)} aria-label="Open navigation"><Menu /></button>
          <div className="topbar-context">
            <span className="topbar-location-icon" aria-hidden="true"><MapPin /></span>
            <span><span className="eyebrow">{locationContext.label}</span><strong>{locationContext.value}</strong></span>
          </div>
          <div className="account-chip"><span className="avatar">{initials(user.displayName)}</span><span><strong>{user.displayName}</strong><small>{formatRole(user.role)}</small></span></div>
          <button className="secondary-button" onClick={() => void logout()}><LogOut />Logout</button>
        </header>
        <main className="content">{children}</main>
        {isDashboard ? <footer className="portal-footer">© {new Date().getFullYear()} RBIM Online. All rights reserved.</footer> : null}
      </div>
    </div>
  );
}

function readSidebarCollapsed() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(sidebarStorageKey) === "true";
  } catch {
    return false;
  }
}

function isDesktopSidebarViewport() {
  return typeof window === "undefined" || window.matchMedia("(min-width: 761px)").matches;
}

function initials(name: string) { return name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase(); }
function formatRole(role: string) {
  if (role === "BARANGAY_ENCODER") return "Barangay Encoder";
  if (role === "MAYOR_VIEWER") return "Mayor Viewer";
  if (role === "BARANGAY_WEB_PORTAL") return "Legacy Barangay Web Portal";
  if (role === "MUNICIPAL_CITY_USER") return "Municipality/City User";
  if (role === "MUNICIPAL_CITY_ADMINISTRATOR") return "Municipality/City Administrator";
  return role.split("_").map((part) => part[0] + part.slice(1).toLowerCase()).join(" ");
}
